import { Anchor, Box, Divider, Flex, List } from '@mantine/core'

import { Text } from '@/components/Text/Text'
import { Title } from '@/components/Title/Title'

import classes from './PrivacyPolicy.module.css'

export function Component() {
  return (
    <Box className={classes.container}>
      <Flex className={classes.header}>
        <Title variant="h1">Privacy Policy for Rolan Wealth</Title>

        <Text variant="body_1_grey">
          Effective Date: October 29, 2026 &nbsp;|&nbsp; Last Updated: October
          29, 2026
        </Text>
      </Flex>

      <Title variant="h2">Summary of Key Points</Title>

      <Text>
        This summary is provided for convenience. The full legal terms follow
        below and govern in all cases.
      </Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>
            We collect your name, email address, and the financial information
            you choose to enter (like your salary, expenses, and account
            balances) to power the Platform.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            When you connect a financial account through Plaid, we do not store
            your bank login credentials, account balances, or transaction
            history. We store only your account name, institution, last four
            digits of the account number, and a secure access token. Nothing
            more.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            If you add an account manually (for example, entering your home
            value), we store the balance and account details you enter. All
            manually entered data is encrypted at rest.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            Your employer receives only your Enrollment Information: your name,
            work email address, enrollment date, and, if applicable, the date
            your access ended. You consent to this when you enroll, and your
            employer may use it only to administer the program and for its own
            tax, payroll, and benefits reporting. We never share your financial
            data, your account activity, your individual answers to our
            enrollment, onboarding, and survey questions, or what you discuss
            with a CFP® professional with your employer. Program reports to
            your employer are aggregate and de-identified, never include
            demographic breakdowns, and never describe fewer than five
            participants.
          </Text>
        </List.Item>

        <List.Item>
          <Text>We do not sell your personal information. Ever.</Text>
        </List.Item>

        <List.Item>
          <Text>
            If you delete your account, your data is deleted immediately from
            our active systems. You are in control. If your access ends because
            you leave your employer, withdraw, or the program ends, we delete
            your remaining data within thirty (30) days. We keep only a limited
            record of your Enrollment Information for program recordkeeping (see
            Section 7).
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            We use cookies to keep the Platform running and, through Google
            Analytics, to understand how the Platform is used. Our marketing
            website (www.rolanwealth.com) does not use cookies for analytics,
            advertising, or tracking. Emails we send may also contain tracking
            pixels that record whether an email was opened and which links were
            clicked. On the Platform, we do not currently have a cookie consent
            banner; we are working to implement one. You can manage cookies
            through your browser settings, and you can disable email tracking by
            turning off automatic image loading in your email client.
          </Text>
        </List.Item>
      </List>

      <Title mt={20} variant="h2">
        Introduction
      </Title>

      <Text>
        CLZ Ventures LLC, a New York limited liability company doing business as
        Rolan Wealth (&ldquo;Rolan Wealth,&rdquo; &ldquo;we,&rdquo;
        &ldquo;us,&rdquo; or &ldquo;our&rdquo;), provides an AI-powered
        financial wellness platform supported by CERTIFIED FINANCIAL
        PLANNER&trade; (CFP®) professionals, designed to help employees achieve
        their financial goals. The Platform is made available to employees as a
        benefit sponsored by their employer (&ldquo;Platform&rdquo; or
        &ldquo;Services&rdquo;).
      </Text>

      <Text mt={8}>
        This Privacy Policy also applies to our public marketing website at
        www.rolanwealth.com (the &ldquo;Website&rdquo;), which is separate from
        the Platform (located at app.rolanwealth.com). The Website is for
        employers, benefits brokers, employees, and other visitors who want to
        learn about Rolan Wealth, read our blog, request a demo, inquire about
        partnering with us, or subscribe to our newsletter. The information we
        collect through the Website, and how we use cookies and similar
        technologies there, are described in Sections 1(A), 1(B), and 5.
      </Text>

      <Text mt={8}>
        This Privacy Policy explains how we collect, use, disclose, retain, and
        protect personal information about individuals who use the Services,
        including employees of our employer clients (&ldquo;Employees&rdquo; or
        &ldquo;you&rdquo;). Please read this Privacy Policy carefully. This
        Privacy Policy also applies to employees who enroll in, or join the
        waitlist for, an employer-sponsored program, and to any spouse or
        partner who attends a CFP® session with a participant.
      </Text>

      <Text mt={8}>
        By accessing or using the Services, you acknowledge that you have read,
        understood, and agree to the collection, use, and disclosure of your
        personal information as described in this Privacy Policy. If you do not
        agree, please discontinue use of the Services immediately.{' '}
        <strong>
          This Privacy Policy does not govern the employment relationship
          between you and your employer.
        </strong>
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">1. Information We Collect</Title>

      <Text>
        We collect personal information from and about you in the categories
        described below. &ldquo;Personal information&rdquo; means any
        information that identifies, relates to, describes, or could reasonably
        be linked to you as an individual.
      </Text>

      <Title mt={20} variant="h3">
        A. Information You Provide Directly
      </Title>

      <Text>When you register for or use the Services, you may provide:</Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>
            <strong>Identification Information:</strong> Full name, work email
            address (used as your primary account identifier), and account
            credentials (username and password). Through your Profile settings,
            you may optionally provide a personal email address and a phone
            number. These fields are not required to use the Platform and are
            collected solely if you choose to provide them.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Enrollment Information:</strong> When you enroll in an
            employer-sponsored program, or join its waitlist, through our online
            enrollment form, we collect your name, work email address, your
            confirmation that you are an eligible employee, and your consent
            choices. We also record your enrollment date and, if applicable, the
            date your access ended. Your name, work email address, enrollment
            date, and access end date are referred to together as your
            &ldquo;Enrollment Information&rdquo;. See Section 4(B) for how
            Enrollment Information is shared with your employer.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Enrollment Questionnaire Responses:</strong> The enrollment
            form also asks a few short questions, such as how confident you feel
            managing your finances, which tools you currently use, which topics
            you would like help with, and, optionally, how often you have felt
            stressed about your finances. These responses help your CFP®
            professional prepare for your sessions. We also combine them with
            other participants&rsquo; responses, and with responses to program
            surveys, to produce aggregate, de-identified program reports for
            your employer, as described in Section 4(B). Your individual
            responses are never shared with your employer.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Program Survey Responses:</strong> We may ask you to
            complete short, optional surveys during or after the program, such
            as a post-session rating or a closing survey. We use your responses
            to improve the program and, in aggregate, de-identified form, in
            program reports to your employer. Your individual responses are
            never shared with your employer.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Financial Information (Plaid-Connected Accounts):</strong>{' '}
            Where you connect a financial account through Plaid, Inc., we store
            your account name, institution name, and the last four digits of
            your account number as account-level identifiers. We also store an
            encrypted Plaid access token to authorize ongoing data retrieval.
            Account balances and transaction data are retrieved on-demand from
            Plaid and displayed on the Platform but are not independently
            stored, with one limited exception: where you modify a transaction
            (e.g., recategorizing, renaming, or excluding it), we store only the
            Plaid-issued transaction ID and your modification for reference.
            Underlying transaction details are not stored. We do not store your
            bank login credentials. Please see Section 1(C) for further detail
            on Plaid data practices.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Financial Information (Manually Added Accounts):</strong>{' '}
            Where Plaid does not support a financial institution or account
            type, you may add accounts manually. For manually added accounts, we
            store the institution name, account name, account type, current
            balance, balance date, initial value, initial value date, estimation
            method, and any notes you choose to enter. All manually entered
            account data is encrypted at rest using AES-256 encryption. If a
            database breach were to occur, this data cannot be decoded without
            the encryption key.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Onboarding Form Information:</strong> Prior to scheduling a
            CFP® session, you will be asked to complete an onboarding form.
            This form requires financial information, including salary and
            expense data, to enable your CFP® professional to prepare for your
            session. The following fields are optional and may be left blank
            without any effect on your access to the Platform or any of its
            features: date of birth; demographic information, including age
            range and household size; gender; and racial or ethnic origin.
            Optional demographic information is used only for Rolan
            Wealth&rsquo;s own aggregate, de-identified analysis to evaluate and
            improve our programming. It is not included in reports to your
            employer and is never shared with your employer in any form. See
            Section 4(B) for the complete disclosure on employer data sharing.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>CFP® Session and View Access Information:</strong>{' '}
            Scheduling details, session topics, notes, and communication records
            associated with consultations with CFP® professionals made
            available through the Platform. Note that scheduling functionality
            may be facilitated through Calendly, Inc. (&ldquo;Calendly&rdquo;),
            a third-party scheduling tool; please review Calendly&rsquo;s
            privacy policy at{' '}
            <Anchor
              href="https://calendly.com/legal/privacy-notice"
              target="_blank"
              variant="primary"
            >
              https://calendly.com/legal/privacy-notice
            </Anchor>
            . When booking a CFP® appointment through Calendly, you may be
            asked to indicate the financial topics you wish to discuss. This
            information is collected by Calendly on our behalf and may be shared
            with Rolan Wealth to prepare your CFP® professional for your
            session. Additionally, if you voluntarily grant a CFP® professional
            &ldquo;View Access&rdquo; to your Platform account, that
            professional will have limited, read-only visibility into the
            following areas of your account: your dashboard, budget page, action
            plan pages, My Accounts page, and your onboarding questionnaire
            responses. CFP® View Access is granted solely to enable your CFP®
            professional to provide you with informed, personalized financial
            guidance based on your complete financial profile. CFP® View Access
            does not expose your linked financial account credentials, raw bank
            login data, or any ability to initiate transactions or interact with
            your financial institutions. You may revoke View Access at any time
            through your account settings, at which point the CFP®
            professional&rsquo;s access will be terminated immediately. Your
            CFP® professional may keep session notes to prepare for and follow
            up on your sessions. Where your employer&rsquo;s program permits, a
            participant&rsquo;s spouse or partner may attend a CFP® session; if
            they do, we and the CFP® professional may receive information about
            them that you or they share during the session, which we protect in
            the same way as your other session information. Spouses and partners
            do not receive their own Platform accounts.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Payment Information:</strong> Where your program offers
            additional CFP® sessions for purchase and you buy them yourself,
            you pay Rolan Wealth directly at booking. Payments are processed by
            our third-party payment processor, which receives your payment card
            details directly. We receive confirmation of payment and limited
            transaction details, such as the amount and date, and do not store
            your full payment card number. Sessions you purchase yourself are
            not reported to your employer.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Program Event Information:</strong> If you attend a live
            program event, such as a launch Q&amp;A or webinar, we may record it
            and make the recording available to other participants or eligible
            employees. Your name, image, voice, and any questions you ask during
            a recorded event may appear in the recording. You may keep your
            camera off and choose not to ask questions during the live event.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Identity Verification Information:</strong> We do not
            currently collect government-issued identification numbers or
            similar identity verification data. If we implement identity
            verification in the future, this Policy will be updated accordingly.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Website Inquiries and Newsletter Subscriptions:</strong> If
            you request a demo, submit a partner inquiry, or subscribe to our
            newsletter through the Website, we collect the information you
            enter, such as your name, work email address, company or
            organization, job title, and any message you write. Demo and partner
            inquiries are received through Formspree, Inc.
            (&ldquo;Formspree&rdquo;), a form service provider, and delivered to
            us by email; Formspree&rsquo;s practices are governed by its privacy
            policy, available at{' '}
            <Anchor
              href="https://formspree.io/legal/privacy-policy"
              target="_blank"
              variant="primary"
            >
              https://formspree.io/legal/privacy-policy
            </Anchor>
            . Newsletter subscriptions are processed by Brevo (see Section 5).
            We use this information to respond to your inquiry, provide the
            information or demonstration you requested, send you the newsletter
            if you subscribe, and manage our business relationships. We do not
            sell this information. You can unsubscribe from the newsletter at
            any time using the link in any newsletter email.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Voluntary Submissions:</strong> Profile photographs or
            images optionally uploaded to your account profile, survey
            responses, feedback, referrals, and other information you choose to
            provide. Profile image upload is optional; if uploaded, your profile
            image is stored on our servers and is visible within your own
            account only.
          </Text>
        </List.Item>
      </List>

      <Title mt={20} variant="h3">
        B. Information Collected Automatically
      </Title>

      <Text>
        When you access or use the Services, we automatically collect:
      </Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>
            <strong>Log and Usage Data:</strong> IP address, browser type,
            operating system, timestamps, pages and features accessed,
            click-stream data, and system activity logs. We also log user
            sign-in and sign-out events to measure session duration and Platform
            engagement. This data is used solely for product analytics and
            Platform improvement and is not shared with your employer in
            individually identifiable form.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Device Data:</strong> Hardware model, device and application
            identifiers, mobile carrier, and system configuration information.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Cookies and Tracking Technologies:</strong> See Section 5
            (Cookies and Tracking Technologies) for details.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Website Visit Data:</strong> When you visit the Website, our
            website host, Cloudflare, Inc. (&ldquo;Cloudflare&rdquo;), processes
            basic technical information, such as your IP address, browser type,
            device type, and the pages you request, in order to deliver the
            Website to you and protect it from attacks and abuse. We also use
            Cloudflare Web Analytics to understand, in aggregate, how many
            people visit the Website, which pages they view, which sites
            referred them, and how well pages perform. Cloudflare Web Analytics
            does not use cookies or similar browser storage, does not
            fingerprint your device, and is not used to track you across other
            websites. Cloudflare&rsquo;s practices are governed by its privacy
            policy, available at{' '}
            <Anchor
              href="https://www.cloudflare.com/privacypolicy/"
              target="_blank"
              variant="primary"
            >
              https://www.cloudflare.com/privacypolicy/
            </Anchor>
            .
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Error and Diagnostic Data:</strong> We use Sentry, an
            application error monitoring service provided by Functional
            Software, Inc. d/b/a Sentry (&ldquo;Sentry&rdquo;), to detect,
            diagnose, and resolve technical errors on the Platform&rsquo;s
            frontend. Sentry is configured to collect certain personally
            identifiable information as part of its frontend error reporting
            functionality, which may include IP addresses, device identifiers,
            user session identifiers, and event breadcrumbs (records of user
            actions and navigation steps preceding an error). This data is
            transmitted to and processed by Sentry&rsquo;s servers solely for
            error diagnosis and Platform quality assurance purposes.
            Sentry&rsquo;s data handling practices are governed by
            Sentry&rsquo;s Privacy Policy, available at{' '}
            <Anchor
              href="https://sentry.io/privacy/"
              target="_blank"
              variant="primary"
            >
              https://sentry.io/privacy/
            </Anchor>
            .
          </Text>
        </List.Item>
      </List>

      <Title mt={20} variant="h3">
        C. Information From Third Parties
      </Title>

      <Text>
        We may receive limited information from third parties, including:
      </Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>
            <strong>Plaid, Inc.:</strong> When you connect your financial
            accounts through Plaid, account balance and transaction data is
            retrieved on-demand from your financial institutions via
            Plaid&rsquo;s API and displayed on the Platform. Rolan Wealth does
            not independently store raw transaction data, with one limited
            exception: where a user modifies a transaction on the Platform
            (e.g., recategorizing, renaming, or excluding a transaction), Rolan
            Wealth stores only the Plaid-issued transaction ID and the
            user&rsquo;s modification for reference. Underlying transaction
            details are not stored. Rolan Wealth also stores the Plaid access
            token (encrypted at rest using AES-256) to enable ongoing data
            retrieval. Rolan Wealth does not receive or store your bank login
            credentials. Your use of Plaid is subject to Plaid&rsquo;s End User
            Privacy Policy (
            <Anchor
              href="https://plaid.com/legal/end-user-privacy-policy"
              target="_blank"
              variant="primary"
            >
              https://plaid.com/legal/end-user-privacy-policy
            </Anchor>
            ).
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Identity Verification Providers:</strong> To the extent we
            engage identity verification or fraud detection services, those
            providers may receive data necessary to verify your identity. We
            will update this disclosure if and when such services are
            implemented.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Your Employer:</strong> Your employer may provide us with
            limited administrative information, such as eligibility criteria and
            notice that a participant is no longer employed, so that we can
            confirm eligibility and manage access to the program. We use this
            information only to administer the program.
          </Text>
        </List.Item>
      </List>

      <Text mt={20}>
        <strong>IMPORTANT:</strong> Apart from the limited administrative
        information described above, Rolan Wealth does not receive individually
        identifiable employee data from your employer. Your financial account
        data, individual usage data, individual enrollment questionnaire,
        onboarding, and survey responses, demographic information, CFP® session
        content and notes, salary information, and other personal financial
        information are never shared with your employer. Your employer receives
        only your Enrollment Information and aggregate, de-identified program
        reports. See Section 4(B) for the complete disclosure.
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">2. How We Use Your Information</Title>

      <Text>
        We use personal information for the following purposes, each supported
        by a lawful basis under applicable law:
      </Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>
            <strong>Service Delivery:</strong> To create and maintain your
            account, provide personalized financial wellness features, connect
            you with CFP® professionals, and process transactions.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>AI-Powered Insights Generation:</strong> To generate
            automated, rule-based informational observations, notifications, and
            summaries (&ldquo;AI-Powered Insights&rdquo;) derived from your
            financial profile and connected account data. AI-Powered Insights
            may be generated using rule-based logic and, in some cases,
            artificial intelligence or large language model technologies. These
            outputs are informational and educational in nature only and do not
            constitute financial, investment, legal, or tax advice. See our
            Terms and Conditions for the full disclaimer governing AI-Powered
            Insights. We do not use your personal or financial information to
            train machine learning or artificial intelligence models without
            your express written consent.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Security and Fraud Prevention:</strong> To authenticate
            users, detect unauthorized access, prevent fraud, and protect the
            integrity of the Platform.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Analytics and Product Improvement:</strong> To understand
            usage patterns, diagnose technical issues, measure feature
            effectiveness, and develop new services. Analytics are performed on
            aggregated or de-identified data to the greatest extent practicable.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Communications:</strong> To send you transactional
            notifications, service updates, and, with your prior consent,
            marketing communications. You may opt out of marketing
            communications at any time (see Section 9). Where your employer
            permits, we may also send program messages, such as scheduling
            reminders and updates, through your employer&rsquo;s workplace
            messaging platform (for example, Slack). We never discuss your
            personal financial matters through your employer&rsquo;s workplace
            messaging platform, and messages sent there may be accessible to
            your employer under its own policies. You may opt out of
            non-essential program communications at any time.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Legal and Regulatory Compliance:</strong> To fulfill our
            obligations under applicable federal, state, and local laws and
            regulations, respond to legal process, and cooperate with regulatory
            authorities.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Dispute Resolution and Enforcement:</strong> To enforce our
            Terms and Conditions and resolve disputes.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Aggregate Program Reporting:</strong> To produce aggregate,
            de-identified program reports for employer clients, as described in
            Section 4(B). Program reports do not include demographic
            information. Separately, demographic information you optionally
            provide, including gender and racial or ethnic origin, is used only
            for Rolan Wealth&rsquo;s own aggregate, de-identified analysis to
            evaluate and improve the quality and relevance of our programming.
            It is never used for individual profiling, never used to make any
            decision about your access to or experience of the Platform or its
            features, and never shared with your employer or any other third
            party in any form.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Program Administration:</strong> To confirm your enrollment,
            select participants where a program uses random selection, manage
            any waitlist, activate and end access, send you reminders (including
            a reminder if you have not yet activated your account), administer
            the CFP® sessions available through your program (including any
            shared pool of sessions), and provide Enrollment Information to your
            employer as described in Section 4(B).
          </Text>
        </List.Item>
      </List>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">
        3. Restrictions on Use of Your Personal Information
      </Title>

      <Text>We want to be explicit about activities we do not engage in:</Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>
            Rolan Wealth does not sell, rent, or trade your personal information
            to third parties for their independent use, including for targeted
            advertising purposes. &ldquo;Sell&rdquo; has the meaning given to it
            under applicable state privacy laws, including the California
            Consumer Privacy Act (CCPA).
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            Other than your Enrollment Information, which is shared with your
            consent as described in Section 4(B), Rolan Wealth does not share
            individually identifiable personal information or financial data
            with your employer or any affiliated entity of your employer.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            Rolan Wealth does not use your personal information to make
            automated decisions that produce legal or similarly significant
            effects without human review and appropriate disclosures.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            Rolan Wealth does not use your personal or financial information to
            train machine learning or artificial intelligence models without
            your express written consent.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            Our agreements with employer clients prohibit your employer from
            requesting or attempting to access your financial or Platform data,
            and from using your enrollment or participation in a program as a
            factor in any employment decision about you.
          </Text>
        </List.Item>
      </List>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">4. How We Share Your Information</Title>

      <Text>
        We share personal information only in the limited circumstances
        described below:
      </Text>

      <Title mt={20} variant="h3">
        A. Service Providers
      </Title>

      <Text>
        We engage third-party vendors and service providers who process personal
        information on our behalf to support Platform operations. These
        providers are contractually required to: (i) use personal information
        solely as directed by us; (ii) implement appropriate technical and
        organizational security measures; and (iii) not disclose personal
        information to additional parties without our authorization.
      </Text>

      <Text mt={8}>
        Current categories of service providers include: cloud hosting and
        infrastructure providers; website hosting, security, and cookie-free web
        analytics services (Cloudflare, Inc.); financial data aggregation
        services (Plaid); scheduling services (Calendly); video conferencing
        services; analytics providers (Google Analytics); error monitoring
        services (Sentry); online enrollment and form services (Tally BV);
        website inquiry form services (Formspree, Inc.); survey services (Google
        LLC); payment processing services; artificial intelligence and large
        language model service providers; email services (SendGrid, provided by
        Twilio Inc., for transactional emails; and Brevo, for program
        communications and reminders and for Website newsletter subscriptions);
        and communications platforms (SMS and other communications, and your
        employer&rsquo;s workplace messaging platform where your employer
        permits).
      </Text>

      <Text mt={8}>
        Enrollment in employer-sponsored programs is facilitated through an
        online form hosted by Tally BV (&ldquo;Tally&rdquo;), a form service
        provider based in Belgium. Information you submit through the form is
        processed by Tally on our behalf in accordance with Tally&rsquo;s
        privacy policy (
        <Anchor
          href="https://tally.so/privacy"
          target="_blank"
          variant="primary"
        >
          https://tally.so/privacy
        </Anchor>
        ) and data processing terms, and is stored on servers located in the
        European Union. Rolan Wealth uses this information only to process your
        enrollment, manage the waitlist, provision access to the Platform, help
        your CFP® professional prepare, and evaluate the program as described
        in this Policy.
      </Text>

      <Text mt={8}>
        <strong>CFP® Professionals:</strong> CFP® sessions are provided by
        CFP® professionals who may be employees or independent contractors of
        Rolan Wealth. We share with your CFP® professional the information
        needed to prepare for and conduct your sessions, such as your name,
        contact details, enrollment questionnaire and onboarding responses, and
        session topics, along with any information you make available through
        View Access. CFP® professionals are bound by confidentiality and data
        security obligations, may use your information only to provide sessions
        to you through Rolan Wealth, and may not use it to solicit you for any
        separate business of their own.
      </Text>

      <Title mt={20} variant="h3">
        B. Employer Clients: Enrollment Information and Aggregate Reports
      </Title>

      <Text>
        <strong>Enrollment Information.</strong> When you enroll in an
        employer-sponsored program, you consent, as a condition of enrollment,
        to Rolan Wealth sharing your Enrollment Information with your employer:
        your name, work email address, enrollment date, and, if applicable, the
        date your access ended. We provide current Enrollment Information to
        your employer&rsquo;s designated program contact on the schedule set in
        our agreement with your employer (for example, monthly). Your employer
        may use Enrollment Information only to administer the program (for
        example, to notify us when a participant leaves the company) and for its
        own tax, payroll, and benefits reporting. Our agreements with employer
        clients require them to limit access to Enrollment Information to
        personnel with a need to know, keep it confidential, and not use your
        enrollment or participation as a factor in any employment decision about
        you. Enrollment Information is disclosed at your direction and is not
        sold.
      </Text>

      <Text mt={8}>
        <strong>Aggregate Program Reports.</strong> We may provide your employer
        with aggregate, de-identified program reports, such as enrollment and
        participation counts, CFP® session volume (excluding any sessions you
        purchase yourself), aggregated session topic themes, aggregated
        responses to enrollment questionnaire and program survey questions, and
        aggregated satisfaction feedback, along with periodic updates on program
        capacity and enrollment status. Program reports never include
        demographic information, any figure describing fewer than five (5)
        participants, or any information reasonably capable of identifying you,
        and we withhold any figure that could be used to calculate a withheld
        figure. We do not provide your employer with individual-level reporting
        or access to the Platform.
      </Text>

      <Text mt={8}>
        <strong>What Your Employer Never Receives.</strong> Your financial
        account data, account balances and transactions, salary and expense
        information, individual usage data, individual enrollment questionnaire,
        onboarding, and survey responses, demographic information, CFP® session
        content and notes, and information about any sessions you purchase
        yourself are never shared with your employer.
      </Text>

      <Title mt={20} variant="h3">
        C. Legal Requirements and Protection of Rights
      </Title>

      <Text>
        We may disclose personal information if required to do so by law or in
        the good-faith belief that such disclosure is reasonably necessary to:
      </Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>
            Comply with a legal obligation, court order, subpoena, or valid
            legal process;
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            Protect the rights, property, or safety of Rolan Wealth, our users,
            or the public;
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            Detect, prevent, or address fraud, security, or technical issues; or
          </Text>
        </List.Item>

        <List.Item>
          <Text>Enforce our Terms and Conditions or other agreements.</Text>
        </List.Item>
      </List>

      <Title mt={20} variant="h3">
        D. Business Transfers
      </Title>

      <Text>
        In the event of a merger, acquisition, reorganization, bankruptcy, or
        sale of all or a portion of our assets, personal information may be
        transferred to the acquiring entity as part of such transaction. We will
        provide notice via email or a prominent notice on the Platform prior to
        personal information becoming subject to a materially different privacy
        policy, and will afford users the opportunity to exercise applicable
        rights.
      </Text>

      <Title mt={20} variant="h3">
        E. With Your Consent
      </Title>

      <Text>
        We may share your personal information with third parties not described
        above when you provide explicit consent to do so.
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">5. Cookies and Tracking Technologies</Title>

      <Text>
        We use cookies, web beacons, and similar technologies to support
        Platform functionality and gather analytics. This section describes the
        cookies currently in use and your options for managing them. Unless we
        say otherwise, the descriptions below of first-party cookies,
        third-party cookies, and analytics apply to the Platform. Our Website is
        described separately under &ldquo;Marketing Website&rdquo; below.
      </Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>
            <strong>Marketing Website (www.rolanwealth.com):</strong> The
            Website does not use cookies for analytics, advertising,
            personalization, or cross-site tracking. It does not load
            third-party advertising, social media, or retargeting tools, and the
            fonts and other resources it displays are hosted on the Website
            itself. Our only analytics on the Website are provided by Cloudflare
            Web Analytics, which measures aggregate traffic without cookies,
            browser storage, or device fingerprinting. Our website host,
            Cloudflare, may use technically necessary security measures to
            detect bots and attacks; if any of these set a cookie, it is used
            only for security and not for tracking or advertising. Because the
            Website does not use cookies that require your consent, it does not
            display a cookie banner. Because the Website does not track visitors
            across other websites, browser signals such as Do Not Track and
            Global Privacy Control do not change how it operates. If we add
            cookies or similar tracking technologies to the Website in the
            future, we will update this Privacy Policy and, where required by
            law, ask for your consent first.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>First-Party Functional Cookies:</strong> Rolan Wealth uses
            first-party cookies that are necessary for the Platform to operate.
            These include session cookies (which expire when you close your
            browser) used for authentication and secure session management, and
            persistent cookies used to remember your login state and user
            preferences. These cookies are essential to the functioning of the
            Platform and cannot be disabled without impairing your ability to
            use the Services.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Third-Party Cookies:</strong> Third-party services
            integrated into the Platform, including Plaid (financial account
            connectivity) and analytics tools such as Google Analytics, may set
            their own cookies on your device when you interact with their
            features. Rolan Wealth does not control these cookies. Plaid&rsquo;s
            cookie and tracking practices are governed by Plaid&rsquo;s Privacy
            Policy (
            <Anchor
              href="https://plaid.com/legal/privacy-policy"
              target="_blank"
              variant="primary"
            >
              https://plaid.com/legal/privacy-policy
            </Anchor>
            ). Google Analytics&rsquo; practices are governed by Google&rsquo;s
            Privacy Policy (
            <Anchor
              href="https://policies.google.com/privacy"
              target="_blank"
              variant="primary"
            >
              https://policies.google.com/privacy
            </Anchor>
            ).
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Web Beacons and Email Tracking:</strong> Transactional
            emails sent by Rolan Wealth (such as account signup confirmations
            and password reset emails) are delivered via SendGrid, a
            transactional email service provided by Twilio Inc. SendGrid&rsquo;s
            default configuration may embed tracking pixels (web beacons) in
            outgoing emails that record whether an email was opened and whether
            any links were clicked. This data is used solely to confirm delivery
            and diagnose technical issues with email delivery, not for marketing
            or profiling purposes. If you wish to prevent this tracking, you may
            disable automatic image loading in your email client settings, which
            will prevent tracking pixels from firing. SendGrid&rsquo;s data
            practices are governed by Twilio&rsquo;s Privacy Policy, available
            at{' '}
            <Anchor
              href="https://www.twilio.com/en-us/legal/privacy"
              target="_blank"
              variant="primary"
            >
              https://www.twilio.com/en-us/legal/privacy
            </Anchor>
            . Program emails, such as welcome messages and session reminders,
            are sent through Brevo and may include tracking pixels and link
            tracking that record whether an email was opened and which links
            were clicked. We use this information to improve our communications
            and, in aggregate form only, to measure program engagement; it is
            never shared with your employer in individual form. Disabling
            automatic image loading also prevents these tracking pixels from
            firing. Brevo&rsquo;s practices are governed by its privacy policy,
            available at{' '}
            <Anchor
              href="https://www.brevo.com/legal/privacypolicy/"
              target="_blank"
              variant="primary"
            >
              https://www.brevo.com/legal/privacypolicy/
            </Anchor>
            . Newsletter emails that you request through the Website are also
            sent through Brevo and may include the same open and click tracking,
            which we use in aggregate to understand which articles interest
            readers.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Cookie Consent Banner (Platform):</strong> Rolan Wealth does
            not currently operate a cookie consent banner or preference
            management tool. We are working to implement a cookie consent
            mechanism as part of our ongoing compliance development. In the
            meantime, you may manage or refuse cookies through your browser
            settings at any time. Note that disabling essential first-party
            cookies will impair your ability to log in and use the Platform. For
            instructions on managing cookies in your browser, visit{' '}
            <Anchor
              href="https://www.allaboutcookies.org"
              target="_blank"
              variant="primary"
            >
              https://www.allaboutcookies.org
            </Anchor>
            .
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Google Analytics Opt-Out:</strong> To opt out of Google
            Analytics tracking across all websites, you may install the Google
            Analytics Opt-Out Browser Add-on available at{' '}
            <Anchor
              href="https://tools.google.com/dlpage/gaoptout"
              target="_blank"
              variant="primary"
            >
              https://tools.google.com/dlpage/gaoptout
            </Anchor>
            .
          </Text>
        </List.Item>
      </List>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">6. Data Security</Title>

      <Text>
        We implement administrative, technical, and physical safeguards designed
        to protect personal information against unauthorized access, disclosure,
        alteration, and destruction. The following describes our current
        security posture. Where a measure is described as planned or in
        development, it is not yet operational.
      </Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>
            <strong>Encryption in Transit:</strong> All communications between
            users and the Platform, and between the Platform and third-party
            data providers (including Plaid), are encrypted using TLS (Transport
            Layer Security).
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Encryption at Rest:</strong> All sensitive data stored by
            Rolan Wealth is encrypted at rest using AES-256 encryption. This
            includes Plaid access tokens, Plaid-connected account metadata,
            manually entered account data (balances, institution details, and
            all fields described in Section 1(A)), user-modified transaction
            records, onboarding form data, and messages exchanged between users
            and CFP® professionals through the Platform&rsquo;s messaging
            feature. In the event of an unauthorized database access, stored
            data cannot be decoded without the encryption key.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Role-Based Access Controls (RBAC):</strong> Access to
            personal information is restricted to authorized personnel based on
            role and business need, including strict IAM (Identity and Access
            Management) controls on cloud infrastructure functions.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Security Monitoring:</strong> We use real-time monitoring
            tools, including AWS CloudWatch and Cloudflare, to detect suspicious
            activity and potential security incidents. We conduct regular
            vulnerability scans and audit IAM roles and permissions. DDoS
            protection is provided via Cloudflare.
          </Text>
        </List.Item>
      </List>

      <Text mt={20}>
        No method of transmission over the Internet or electronic storage system
        is completely secure. We cannot guarantee absolute security of your
        information, and you bear responsibility for maintaining the
        confidentiality of your account credentials. You should notify us
        immediately at privacy@rolanwealth.com if you suspect any unauthorized
        access to or use of your account.
      </Text>

      <Text mt={8}>
        <strong>Security Development:</strong> We continuously evaluate and
        improve our security posture. Planned enhancements to our Platform
        security include additional account authentication controls and
        anomalous login detection. We will update this Privacy Policy to reflect
        material changes to our security practices as they are implemented.
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">7. Data Retention</Title>

      <Text>
        We retain personal information for as long as your account remains
        active and for a period thereafter as necessary to fulfill the purposes
        described in this Privacy Policy or to comply with applicable legal
        obligations. Specifically:
      </Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>
            <strong>Active Accounts:</strong> Personal information is retained
            for the duration of your active account.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>End of Program Access:</strong> Your access to an
            employer-sponsored program ends if you leave your employer, no
            longer meet your program&rsquo;s eligibility requirements, withdraw
            from the program, or the program ends or is terminated. Where
            another eligible employee is waiting for a place, your access may
            also end if you have not activated your account within the period
            described in your program details (for example, thirty (30) days
            after the later of your enrollment confirmation or the program
            launch), and you still have not activated it after we send you a
            reminder. When your access ends, we delete your remaining personal
            data from our active systems within thirty (30) days, other than
            your Enrollment Information as described below. If your employer
            renews the program before it ends, your account continues.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Account Deletion:</strong> When you delete your account,
            whether through the in-app deletion feature or by written request to
            privacy@rolanwealth.com, all of your personal data is deleted
            immediately from our active database. This includes your profile
            information (name, email addresses, phone number, and profile
            image), manually entered account data, Plaid access tokens and
            account metadata, onboarding form responses, user-modified
            transaction records, and messages exchanged with CFP®
            professionals. If you were enrolled in an employer-sponsored
            program, we retain only your Enrollment Information, as described
            below. Other than Enrollment Information, no retention period
            applies following account deletion. Residual copies in automated
            backup systems are purged in the ordinary course of backup rotation.
            If you have a legal basis to require earlier backup deletion, please
            contact us.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Linked Financial Account Data and Tokens:</strong> Plaid
            access tokens, Plaid-connected account metadata (account name,
            institution, last four digits), and user-modified transaction
            records are all deleted immediately upon account deletion as part of
            the active database purge described above. No separate retention
            period applies to this data.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>CFP® Session Notes:</strong> CFP® session notes are
            deleted on the same schedule as your other personal data, including
            by any CFP® professional who holds them.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Enrollment Information:</strong> We retain Enrollment
            Information for program administration and recordkeeping, including
            to respond to your employer&rsquo;s reasonable requests for its tax,
            payroll, and benefits reporting, for up to three (3) years after the
            end of the program, or longer if required by law. Enrollment
            Information does not include any of your financial or Platform data.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Waitlist Information:</strong> If you join a waitlist and
            are not offered a place, we delete your waitlist information within
            thirty (30) days after the program ends.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Website Inquiries and Newsletter Subscriptions:</strong> We
            keep demo and partner inquiries for as long as reasonably necessary
            to respond to you and manage any resulting business relationship,
            and delete them on request. We keep newsletter subscriber
            information until you unsubscribe, after which we retain only the
            minimum information needed to honor your opt-out.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Aggregate and De-Identified Data:</strong> We may retain and
            use de-identified or aggregated data derived from your information
            indefinitely, as such data is no longer personal information. We
            maintain de-identified data in de-identified form and do not attempt
            to re-identify it.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Legal Holds:</strong> Where required by law, regulation, or
            valid legal process, we may be obligated to retain certain
            information beyond the periods described above.
          </Text>
        </List.Item>
      </List>

      <Text mt={20}>
        To delete your personal information, use the account deletion feature
        within the Platform, which deletes all of your data immediately from our
        active database. Alternatively, submit a written deletion request to
        privacy@rolanwealth.com and we will process it promptly. Where a written
        request requires identity verification before processing, we will
        complete deletion within forty-five (45) days of verification. Deletion
        from automated backup systems occurs in the ordinary course of backup
        rotation. Legal holds, if any, are described in the bullet above.
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">8. Data Breach Notification</Title>

      <Text>
        In the event of a security breach that compromises personal information,
        we will notify affected individuals promptly and in accordance with the
        requirements of applicable federal and state data breach notification
        laws. Notifications will be provided via email to the address associated
        with your account and/or through conspicuous notice on the Platform. We
        will describe the nature of the breach, the information affected, and
        the steps we are taking to address it. If an incident affects
        information relating to an employer-sponsored program, we will also
        notify your employer that the incident occurred, as our agreement with
        your employer requires.
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">9. Your Privacy Rights</Title>

      <Text>
        Depending on your jurisdiction and applicable law, you may have one or
        more of the following rights with respect to your personal information:
      </Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>
            <strong>Right to Know / Access:</strong> The right to request
            disclosure of the categories and specific pieces of personal
            information we have collected about you, the sources of collection,
            the purposes for which it is used, and the categories of third
            parties with whom it is shared.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Right to Correct:</strong> The right to request correction
            of inaccurate personal information we maintain about you.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Right to Delete:</strong> The right to request deletion of
            personal information we have collected about you, subject to certain
            legal exceptions.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Right to Opt Out of Sale / Sharing:</strong> The right to
            opt out of the sale of your personal information or its sharing for
            targeted advertising purposes. As stated in Section 3, Rolan Wealth
            does not sell personal information. If our practices change, we will
            update this Policy and provide a clear opt-out mechanism.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Right to Non-Discrimination:</strong> You have the right not
            to receive discriminatory treatment for exercising any privacy
            rights described in this Policy.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Right to Data Portability:</strong> In certain
            jurisdictions, the right to receive a copy of your personal
            information in a structured, commonly used, machine-readable format.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Right to Restrict Processing / Object:</strong> In certain
            jurisdictions, the right to restrict or object to our processing of
            your personal information under certain circumstances.
          </Text>
        </List.Item>
      </List>

      <Text mt={20}>
        To exercise any of these rights, please submit a request to
        privacy@rolanwealth.com. We will verify your identity before processing
        your request and will respond within the timeframe required by
        applicable law (typically 45 days, with a possible extension of an
        additional 45 days upon notice to you). We will not charge a fee for
        responding to your request unless it is excessive, repetitive, or
        manifestly unfounded.
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">10. California Privacy Rights (CCPA/CPRA)</Title>

      <Text>
        This section applies to California residents and supplements the rest of
        this Privacy Policy. It is provided pursuant to the California Consumer
        Privacy Act of 2018, as amended by the California Privacy Rights Act of
        2020 (collectively, &ldquo;CCPA&rdquo;).
      </Text>

      <Title mt={20} variant="h3">
        A. Categories of Personal Information Collected
      </Title>

      <Text>
        In the twelve (12) months preceding the effective date of this Policy,
        we have collected the following categories of personal information about
        California residents, as defined by the CCPA:
      </Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>
            <strong>Identifiers:</strong> Name, work email address, personal
            email address (if optionally provided), phone number (if optionally
            provided), IP address, account credentials, and device identifiers.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Personal Information under Cal. Civ. Code §1798.80:</strong>{' '}
            Name, email address(es), phone number (if optionally provided), date
            of birth, and financial account information.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Sensitive Personal Information:</strong> Precise financial
            data including self-reported income, account balances retrieved via
            Plaid, and user-modified transaction records; the contents of CFP®
            session communications; and, where optionally provided through the
            onboarding form, racial or ethnic origin and gender.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Internet or Electronic Network Activity:</strong> Browsing
            history on the Platform, page interactions, feature usage, and
            clickstream data.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Professional or Employment-Related Information:</strong>{' '}
            Your employer, your confirmation that you are an eligible employee,
            your program enrollment and access dates, and any notice that you
            are no longer employed by your employer.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Commercial Information:</strong> Records of CFP® sessions
            you purchase directly from us.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Audio, Electronic, or Visual Information:</strong>{' '}
            Recordings of live program events you attend.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Geolocation Data:</strong> Approximate geographic location
            derived from IP address. We do not collect precise GPS-based
            location data.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Inferences:</strong> Profiles derived from financial data to
            generate personalized wellness insights and recommendations.
          </Text>
        </List.Item>
      </List>

      <Title mt={20} variant="h3">
        B. Purposes of Collection and Use
      </Title>

      <Text>
        We collect and use the above categories of personal information for the
        business and commercial purposes described in Section 2 of this Privacy
        Policy, including service delivery, security, analytics, communications,
        and legal compliance.
      </Text>

      <Title mt={20} variant="h3">
        C. Disclosure of Personal Information
      </Title>

      <Text>
        We disclose the above categories of personal information to the
        categories of third parties described in Section 4 of this Privacy
        Policy (service providers, CFP® professionals, legal authorities, and
        successors in interest) and, for Enrollment Information only, to your
        employer at your direction, as described in Section 4(B), solely for the
        business purposes described herein.
      </Text>

      <Title mt={20} variant="h3">
        D. No Sale or Sharing of Personal Information
      </Title>

      <Text>
        <strong>
          Rolan Wealth does not sell California residents&rsquo; personal
          information for monetary or other valuable consideration, and does not
          share personal information with third parties for cross-context
          behavioral advertising purposes, as those terms are defined under the
          CCPA. Your Enrollment Information is disclosed to your employer at
          your direction, as a condition of enrolling in an employer-sponsored
          program, and is not sold.
        </strong>
      </Text>

      <Title mt={20} variant="h3">
        E. Sensitive Personal Information
      </Title>

      <Text>
        To the extent we collect sensitive personal information as defined by
        the CCPA, we use it solely for purposes reasonably necessary to provide
        the Services. We do not use or disclose sensitive personal information
        for purposes beyond those permitted by the CCPA without your consent.
      </Text>

      <Title mt={20} variant="h3">
        F. California Consumer Rights
      </Title>

      <Text>
        California residents have the following specific rights under the CCPA,
        in addition to those described in Section 9:
      </Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>
            <strong>Right to Know:</strong> You may request that we disclose (i)
            the categories of personal information collected; (ii) the
            categories of sources; (iii) the business or commercial purposes for
            collection; (iv) the categories of third parties with whom personal
            information is shared; and (v) the specific pieces of personal
            information collected about you.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Right to Delete:</strong> You may request deletion of
            personal information we have collected about you, subject to certain
            exceptions (e.g., where retention is required to complete a
            transaction, detect security incidents, or comply with a legal
            obligation).
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Right to Correct:</strong> You may request correction of
            inaccurate personal information.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Right to Opt Out of Sale/Sharing:</strong> As noted above,
            we do not sell or share personal information for behavioral
            advertising. No opt-out mechanism is required at this time; however,
            if our practices change, we will provide a &ldquo;Do Not Sell or
            Share My Personal Information&rdquo; link on our Platform.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>
              Right to Limit Use of Sensitive Personal Information:
            </strong>{' '}
            You may request that we limit our use of your sensitive personal
            information (which for Rolan Wealth consists of precise financial
            data, CFP® session communication contents, and, where optionally
            provided, racial or ethnic origin and gender) to uses reasonably
            necessary for providing the Services, as permitted by the CCPA/CPRA.
            With respect to the optional sensitive PI fields (racial or ethnic
            origin and gender), you may exercise control at the point of
            collection by simply declining to complete those fields in the
            onboarding form. Doing so will not affect your access to the
            Platform or any of its features.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Right to Non-Discrimination:</strong> We will not
            discriminate against you for exercising your CCPA rights, including
            by denying services, charging different prices, or providing a
            different level of service quality.
          </Text>
        </List.Item>
      </List>

      <Title mt={20} variant="h3">
        G. Submitting Requests
      </Title>

      <Text>
        California residents may submit requests to exercise their CCPA rights
        by:
      </Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>
            <strong>Email:</strong> privacy@rolanwealth.com (subject line:
            &ldquo;California Privacy Request&rdquo;)
          </Text>
        </List.Item>
      </List>

      <Text mt={8}>
        We will acknowledge receipt of your request within ten (10) business
        days and respond within forty-five (45) calendar days. We may extend
        this period by an additional forty-five (45) days when reasonably
        necessary, with prior notice. We will verify your identity by matching
        information you provide against information in our records.
      </Text>

      <Text mt={8}>
        <strong>Authorized Agents:</strong> You may designate an authorized
        agent to submit a request on your behalf. We may require you to directly
        verify your identity with us and confirm the agent&rsquo;s
        authorization.
      </Text>

      <Title mt={20} variant="h3">
        H. Shine the Light
      </Title>

      <Text>
        California Civil Code §1798.83 (&ldquo;Shine the Light&rdquo;) permits
        California residents to request, once per calendar year, information
        about the personal information we disclose to third parties for direct
        marketing purposes. Rolan Wealth does not disclose personal information
        to third parties for direct marketing purposes without prior consent.
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">11. State Privacy Rights: Other Jurisdictions</Title>

      <Text>
        Users in certain states may have additional rights under applicable
        state privacy laws, including but not limited to residents of Colorado
        (Colorado Privacy Act), Connecticut (Connecticut Data Privacy Act),
        Virginia (Virginia Consumer Data Protection Act), Texas (Texas Data
        Privacy and Security Act), and other states with enacted consumer
        privacy legislation. These rights generally parallel those described in
        Sections 9 and 10 above, including rights to access, correct, delete,
        opt out of sale/sharing, and opt out of profiling. To exercise
        applicable state privacy rights, please contact privacy@rolanwealth.com.
      </Text>

      <Title mt={20} variant="h3">
        Note on GDPR and International Privacy Laws
      </Title>

      <Text>
        Rolan Wealth is a U.S.-based platform that currently provides Services
        exclusively to employees located in the United States. We do not direct
        our Services to individuals in the European Union, United Kingdom, or
        other jurisdictions subject to the General Data Protection Regulation
        (GDPR) or equivalent international data protection laws. Accordingly,
        GDPR does not currently apply to our operations. If Rolan Wealth expands
        its Services to users in jurisdictions covered by GDPR or similar
        regulations in the future, we will update this Privacy Policy to reflect
        the applicable legal requirements at that time. Some of our service
        providers, such as Tally and Brevo, may store or process information
        outside the United States. Where they do, we require them to protect it
        under their data processing terms.
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">12. Children&rsquo;s Privacy</Title>

      <Text>
        The Services are intended for use by individuals who are at least
        eighteen (18) years of age. We do not knowingly collect, solicit, or use
        personal information from individuals under the age of 18. If we learn
        that we have inadvertently collected personal information from a minor,
        we will take prompt steps to delete such information. If you believe we
        may have collected personal information from a minor, please contact us
        at privacy@rolanwealth.com.
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">13. Third-Party Links and Services</Title>

      <Text>
        The Platform may contain links to third-party websites, applications, or
        services not operated or controlled by Rolan Wealth. This Privacy Policy
        does not apply to third-party services. We encourage you to review the
        privacy policies of any third-party service you access through or in
        connection with our Platform. We are not responsible for the privacy
        practices of any third party.
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">14. Changes to This Privacy Policy</Title>

      <Text>
        We may update this Privacy Policy from time to time to reflect changes
        to our practices, technology, legal requirements, or for other
        operational or business reasons. When we make material changes, we will:
      </Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>
            Update the &ldquo;Last Updated&rdquo; date at the top of this
            Policy;
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            Notify you via email to the address associated with your account;
            and/or
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            Post a conspicuous notice on the Platform or its login page.
          </Text>
        </List.Item>
      </List>

      <Text mt={8}>
        Your continued use of the Services after the effective date of an
        updated Privacy Policy constitutes your acceptance of the changes to the
        extent permitted by applicable law. We encourage you to review this
        Privacy Policy periodically.
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">15. Contact Information</Title>

      <Text>
        For questions, concerns, or requests relating to this Privacy Policy or
        our privacy practices, or to exercise any of your rights described
        herein, please contact us at:
      </Text>

      <Text mt={8}>
        <strong>Rolan Wealth (CLZ Ventures LLC)</strong>
      </Text>

      <Text>Attn: Privacy Officer</Text>

      <Text>54 State Street, Suite 804 #7100</Text>

      <Text>Albany, New York 12207</Text>

      <Text>Email: privacy@rolanwealth.com</Text>

      <Text mt={20}>
        We will acknowledge receipt of your inquiry within five (5) business
        days and will make commercially reasonable efforts to resolve your
        concern or respond to your request in a timely manner.
      </Text>

      <Text mt={20}>
        <em>
          Rolan Wealth is a service of CLZ Ventures LLC © 2026. All rights
          reserved.
        </em>
      </Text>

      <Text mt={8}>
        <em>
          CFP® and CERTIFIED FINANCIAL PLANNER&trade; are certification marks
          owned by Certified Financial Planner Board of Standards, Inc.
        </em>
      </Text>
    </Box>
  )
}

Component.displayName = 'PrivacyPolicy'
